import React, { useEffect, useState } from 'react';
import CardShell from '../../components/CardShell';
import { useP } from '../../i18n/useP';
import { formatNumber } from '../../utils/format';
import { getSalesReceivables } from '../../api/salesOffers';
import './OfferMonthPage.css';

/* LES DÉBITEURS générés par les ventes (2026-09-12), en lecture seule : la
   facturation hors taxes du Plan mensuel par compte, la TVA qui s'y ajoute,
   la facturation TTC, moins les encaissements TTC calculés par les conditions
   de chaque offre, égale la variation des créances clients. C'est cette
   variation que le plan de liquidité devra retrouver. Mois ouverts seulement :
   le réel des mois clos est au bilan. */
const MOIS = (p) => [p('Jan'), p('Fév'), p('Mar'), p('Avr'), p('Mai'), p('Juin'),
                     p('Juil'), p('Août'), p('Sep'), p('Oct'), p('Nov'), p('Déc')];

export default function SalesReceivablesPage({ activeTenant, year, unit = 'kchf', closeAction = null }) {
  const p = useP();
  const tid = activeTenant?.id;
  const an = Number(year) || new Date().getFullYear();
  const [vue, setVue] = useState(null);
  const dev = unit === 'kchf' ? 'kCHF' : 'CHF';

  useEffect(() => {
    let vivant = true;
    if (!tid) return () => {};
    getSalesReceivables(tid, an).then((r) => { if (vivant && !r.error) setVue(r.data); }).catch(() => {});
    return () => { vivant = false; };
  }, [tid, an]);

  const nb = (v) => (v == null ? '' : formatNumber(unit === 'kchf' ? v / 1000 : v, 0));
  const val = (v) => (v == null || Math.abs(v) < (unit === 'kchf' ? 500 : 0.5) ? '–' : nb(v));
  if (!tid) return null;
  const cc = vue ? vue.closed_count : 0;
  /* Le PRÉVISIONNEL seul (2026-09-12) : les mois ouverts, sans réalisé ; on
     est ici pour lire l'impact du plan, les comptes se lisent dans Finance. */
  const ouverts = Array.from({ length: 12 - cc }, (_, k) => cc + k);
  const noms = MOIS(p);
  const total = (s) => ouverts.reduce((a, i) => a + (s[i] || 0), 0);
  /* Le cumul court sur les mois ouverts ; sa colonne de total est son dernier point, pas une somme. */
  const cumul = (s) => { let c = 0; return s.map((v, i) => (ouverts.includes(i) ? (c += (v || 0)) : null)); };
  const rang = (cls, libelle, s, opts = {}) => (
    <tr key={opts.k || (typeof libelle === 'string' ? libelle : cls)} className={`om-row ${cls}`}>
      <td className="om-lbl">{libelle}</td>
      {ouverts.map((i) => <td key={i} className={`lk-num om-calc${opts.fort ? ' om-fort' : ''}`}>{val(s[i])}</td>)}
      <td className={`lk-num om-an${opts.fort ? ' om-fort' : ''}`}>{val(opts.total === 'dernier' ? s[ouverts[ouverts.length - 1]] : total(s))}</td>
    </tr>
  );
  const taux = vue ? `${Math.round(vue.vat_rate * 1000) / 10} %` : '';

  return (
    /* Pas de sous-titre descriptif (standard de carte) : la table dit ce qu'elle montre. */
    <CardShell title={`${p('Débiteurs')} · ${dev}`} primaryAction={closeAction} flush>
      {!vue ? <div className="om-empty">{p('Chargement…')}</div> : (
        <div className="om-scroll lk-hscroll">
          <table className="lk-entry-table om-table om-rm">
            <colgroup>
              <col style={{ width: 300 }} />
              {ouverts.map((i) => <col key={i} style={{ width: 84 }} />)}
              <col style={{ width: 104 }} />
            </colgroup>
            <thead>
              <tr>
                <th className="om-lbl">{p('Flux')}</th>
                {ouverts.map((i) => <th key={i}>{noms[i]}</th>)}
                <th className="om-th2 om-th--fort">{p('Total')}<br />{an}</th>
              </tr>
            </thead>
            <tbody>
              {/* La facturation, ses comptes, puis la TVA SOUS le filet : elle s'ajoute. */}
              {rang('om-offre om-offre--seule', p('Facturation HT'), vue.total.billing, { fort: true })}
              {vue.rows.map((r) => rang('om-li', r.id ? r.label : p('Sans compte'), r.billing, { k: r.id || 'sans-compte' }))}
              {rang('om-li om-rm__plus', `+ ${p('TVA')} ${taux}`, vue.total.vat)}
              {rang('om-tot om-rm__ttc', `= ${p('Facturation TTC')}`, vue.total.ttc)}
              {/* L'encaissement en deux lignes, hors taxes puis TVA encaissée, pour vérifier. */}
              <tr className="om-rm__gap"><td colSpan={ouverts.length + 2} /></tr>
              {rang('om-offre om-offre--seule', p('Encaissements HT'), vue.total.cash_ht, { fort: true })}
              {rang('om-li om-rm__plus', `+ ${p('TVA encaissée')}`, vue.total.cash_vat)}
              {rang('om-tot om-rm__enc', `= ${p('Encaissements TTC')}`, vue.total.cash)}
              {rang('om-tot om-rm__marge', <>{p('Variation des créances clients')}<span className="om-px">({p('facturation TTC')} − {p('encaissements TTC')})</span></>, vue.total.delta, { fort: true })}
              {/* Le cumul depuis le premier mois ouvert : ce que le plan ajoute aux créances du bilan. */}
              {rang('om-li', p('Variation cumulée des créances clients'), cumul(vue.total.delta), { total: 'dernier' })}
            </tbody>
          </table>
        </div>
      )}
    </CardShell>
  );
}
